import type { FileSoundDefinition, GameTheme, GeneratedSoundDefinition } from "../theme/types";

export class SoundBus {
  private context?: AudioContext;
  private files = new Map<string, HTMLAudioElement>();
  private loops = new Map<keyof GameTheme["sounds"], HTMLAudioElement>();
  private retryingLoops = new Set<keyof GameTheme["sounds"]>();
  private unlockListenersBound = false;

  constructor(private readonly theme: GameTheme) {}

  play(key: keyof GameTheme["sounds"]) {
    const definition = this.theme.sounds[key];
    if (!definition) return;

    if (definition.type === "file") {
      this.playFile(definition);
      return;
    }

    const context = this.getContext();
    if (!context) return;

    if (context.state === "suspended") {
      void context.resume();
    }

    if (definition.type === "noise") {
      this.playNoise(context, definition);
      return;
    }

    this.playTone(context, definition);
  }

  playLoop(key: keyof GameTheme["sounds"]) {
    const definition = this.theme.sounds[key];
    if (!definition || definition.type !== "file") return;

    const existing = this.loops.get(key);
    if (existing && !existing.paused) return;

    const audio = this.createAudio(definition.url);
    audio.loop = true;
    audio.volume = definition.volume;
    audio.currentTime = 0;
    this.loops.set(key, audio);
    void audio.play()
      .then(() => this.retryingLoops.delete(key))
      .catch(() => {
        if (this.loops.get(key) === audio) {
          this.retryingLoops.add(key);
          this.bindUnlockListeners();
        }
      });
  }

  stopLoop(key: keyof GameTheme["sounds"]) {
    const audio = this.loops.get(key);
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    this.loops.delete(key);
    this.retryingLoops.delete(key);
  }

  stopAllLoops() {
    for (const key of this.loops.keys()) {
      this.stopLoop(key);
    }
  }

  private getContext() {
    if (this.context) return this.context;

    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) return undefined;

    this.context = new AudioCtor();
    return this.context;
  }

  private playTone(context: AudioContext, definition: GeneratedSoundDefinition) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    const duration = definition.durationMs / 1000;

    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(definition.frequency ?? 440, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(80, (definition.frequency ?? 440) * 0.7), now + duration);
    gain.gain.setValueAtTime(definition.volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  private playNoise(context: AudioContext, definition: GeneratedSoundDefinition) {
    const duration = definition.durationMs / 1000;
    const bufferSize = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, bufferSize, context.sampleRate);
    const data = buffer.getChannelData(0);

    for (let index = 0; index < bufferSize; index += 1) {
      data[index] = (Math.random() * 2 - 1) * (1 - index / bufferSize);
    }

    const source = context.createBufferSource();
    const gain = context.createGain();
    const now = context.currentTime;

    gain.gain.setValueAtTime(definition.volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(context.destination);
    source.start(now);
  }

  private playFile(definition: FileSoundDefinition) {
    const base = this.getBaseAudio(definition.url);
    const audio = base.cloneNode(true) as HTMLAudioElement;
    audio.volume = definition.volume;
    audio.loop = definition.loop ?? false;
    void audio.play().catch(() => undefined);
  }

  private getBaseAudio(url: string) {
    const existing = this.files.get(url);
    if (existing) return existing;

    const audio = this.createAudio(url);
    this.files.set(url, audio);
    return audio;
  }

  private createAudio(url: string) {
    const audio = new Audio(url);
    audio.preload = "auto";
    return audio;
  }

  private bindUnlockListeners() {
    if (this.unlockListenersBound) return;

    this.unlockListenersBound = true;
    const retry = () => {
      this.unlockListenersBound = false;
      this.retryBlockedLoops();
    };

    window.addEventListener("pointerdown", retry, { once: true, capture: true });
    window.addEventListener("keydown", retry, { once: true, capture: true });
  }

  private retryBlockedLoops() {
    for (const key of [...this.retryingLoops]) {
      const audio = this.loops.get(key);
      if (!audio) {
        this.retryingLoops.delete(key);
        continue;
      }

      void audio.play()
        .then(() => this.retryingLoops.delete(key))
        .catch(() => this.bindUnlockListeners());
    }
  }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
